import { CurrencySelect } from '@jurnee/common/src/components/CurrencySelect';
import { DatePicker } from '@jurnee/common/src/components/DatePicker';
import { FileInput } from '@jurnee/common/src/components/FileInput';
import { PriceInput } from '@jurnee/common/src/components/PriceInput';
import { PrimaryButton } from '@jurnee/common/src/components/primitives/buttons/PrimaryButton';
import { SecondaryButton } from '@jurnee/common/src/components/primitives/buttons/SecondaryButton';
import { Divider } from '@jurnee/common/src/components/primitives/Divider';
import { Drawer } from '@jurnee/common/src/components/primitives/Drawer';
import { FormControl } from '@jurnee/common/src/components/primitives/FormControl';
import { useToast } from '@jurnee/common/src/components/primitives/hooks/useToast';
import { Input } from '@jurnee/common/src/components/primitives/Input';
import { HStack } from '@jurnee/common/src/components/primitives/stack/HStack';
import { VStack } from '@jurnee/common/src/components/primitives/stack/VStack';
import { Textarea } from '@jurnee/common/src/components/primitives/Textarea';
import { DOCUMENT_CONTENT_TYPES } from '@jurnee/common/src/constants/ContentTypes';
import { PartnerDocumentUpsertBody } from '@jurnee/common/src/dtos/partnersDocuments';
import { DEFAULT_UTC_TIMEZONE } from '@jurnee/common/src/entities/Address';
import { BookingJSON } from '@jurnee/common/src/entities/Booking';
import { PartnerDocumentJSON } from '@jurnee/common/src/entities/PartnerDocument';
import { PartnerRequestJSON } from '@jurnee/common/src/entities/PartnerRequest';
import { PropositionJSON } from '@jurnee/common/src/entities/Proposition';
import { convertToTimezone, getCurrentTimeZone } from '@jurnee/common/src/utils/dates';
import { isEmpty } from '@jurnee/common/src/utils/strings';
import { createPartnerDocument, uploadPartnerDocument } from 'api/partnerDocuments';
import { createPartnerRequestProposition, updatePartnerRequestProposition } from 'api/partnersRequests';
import { cloneElement, HTMLAttributes, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getPartnerSelector } from 'store/partner/partner.selectors';

export interface QuoteDrawerData {
  proposition: PropositionJSON;
  quote: PartnerDocumentJSON;
}

interface Props {
  bookingId: BookingJSON['id'];
  partnerRequestId: PartnerRequestJSON['id'];
  proposition?: PropositionJSON;
  quote?: PartnerDocumentJSON;
  children: ReactElement<HTMLAttributes<HTMLElement>>;
  onUpsert(data: QuoteDrawerData): void;
}

export function PartnerQuoteDrawer(props: Props) {
  const toast = useToast();
  const { t } = useTranslation(['proposition', 'common']);

  const partner = useSelector(getPartnerSelector);

  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [amount, setAmount] = useState<number | null>(props.quote?.amount ?? null);
  const [taxAmount, setTaxAmount] = useState<number | null>(props.quote?.taxAmount ?? null);
  const [currency, setCurrency] = useState(props.quote?.currency ?? 'EUR');
  const [expiredAt, setExpiredAt] = useState<string | null>(props.proposition?.expiredAt ?? null);
  const [name, setName] = useState<string | null>(props.proposition?.name ?? null);
  const [comment, setComment] = useState<string | null>(props.proposition?.comment ?? null);

  const [file, setFile] = useState<File | null>(null);
  const [hasFile, setHasFile] = useState(!!props.quote?.partnerId);

  const isAmountInvalid = amount !== null && amount <= 0;
  const isTaxAmountInvalid = taxAmount !== null && taxAmount < 0;
  const isNameInvalid = !name || isEmpty(name);

  const isSaveDisabled = !amount || isAmountInvalid || isTaxAmountInvalid || !hasFile || isNameInvalid || !expiredAt;

  const quoteBody: Omit<PartnerDocumentUpsertBody, 'path'> = {
    amount: amount ?? 0,
    taxAmount: taxAmount ?? 0,
    currency,
    dueAt: null,
    documentReference: null,
    type: 'QUOTE',
    filename: file?.name ?? '',
    size: file?.size ?? 0
  };

  function onOpen() {
    setIsOpen(true);
  }

  function onClose() {
    setIsOpen(false);
  }

  async function uploadQuote() {
    if (!partner || !file) {
      throw new Error('Missing required data for upload');
    }
    const { path } = await uploadPartnerDocument(partner.id, props.bookingId, file);
    return createPartnerDocument(partner.id, props.bookingId, { ...quoteBody, path });
  }

  async function create() {
    if (!partner || !expiredAt) {
      throw new Error('Missing required data');
    }
    const quote = await uploadQuote();
    const expiredAtUTC = convertToTimezone(expiredAt, getCurrentTimeZone(), DEFAULT_UTC_TIMEZONE);

    const { data, relationships: { partnersDocuments } } = await createPartnerRequestProposition({
      partnerId: partner.id,
      partnerRequestId: props.partnerRequestId,
      data: {
        name: name ?? '',
        comment,
        expiredAt: expiredAtUTC,
        partnerDocument: {
          id: quote.id
        }
      }
    });

    return { proposition: data, quote: partnersDocuments[0] };
  }

  async function update() {
    if (!partner || !expiredAt || !props.proposition) {
      throw new Error('Missing required data');
    }
    const quote = file ? await uploadQuote() : props.quote;
    if (!quote) {
      throw new Error('No quote available');
    }
    const expiredAtUTC = convertToTimezone(expiredAt, getCurrentTimeZone(), DEFAULT_UTC_TIMEZONE);

    const { data, relationships: { partnersDocuments } } = await updatePartnerRequestProposition({
      partnerId: partner.id,
      partnerRequestId: props.partnerRequestId,
      propositionId: props.proposition.id,
      data: {
        name: name ?? '',
        comment,
        expiredAt: expiredAtUTC,
        partnerDocument: {
          id: quote.id,
          amount: amount ?? 0,
          taxAmount: taxAmount ?? 0,
          currency
        }
      }
    });

    return { proposition: data, quote: partnersDocuments[0] };
  }

  async function onSave() {
    setIsSaving(true);

    try {
      const data = await (props.proposition ? update() : create());
      props.onUpsert(data);
      onClose();
      toast.success(t('drawer.form.toasts.success'));
    } catch(err: unknown) {
      toast.error(t('drawer.form.toasts.error'));
    }

    setIsSaving(false);
  }

  function onDateChange(date: Date | null) {
    if (!date) {
      return;
    }

    setExpiredAt(date.toISOString());
  }

  function onFileChange(file: File) {
    setFile(file);
    setHasFile(true);
  }

  function onFileRemoved() {
    setFile(null);
    setHasFile(false);
  }

  return (
    <>
      { cloneElement(props.children, { onClick: onOpen }) }

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        size="md"
        title={t(`drawer.title.${props.proposition ? 'edit' : 'create'}`)}
        p={5}
        footer={
          <HStack justifyContent="space-between" w="100%">
            <SecondaryButton size="sm" colorScheme="pink" onClick={onClose}>
              { t('common:buttons.close') }
            </SecondaryButton>

            <PrimaryButton size="sm" colorScheme="teal" onClick={onSave} isLoading={isSaving} isDisabled={isSaveDisabled}>
              { t('common:buttons.save') }
            </PrimaryButton>
          </HStack>
        }
      >
        <VStack spacing={5}>
          <FormControl label={t('common:fields.name.label')} isRequired>
            <Input
              size="sm"
              name="name"
              value={name ?? ''}
              placeholder={t(`drawer.form.name.placeholder`)}
              onChange={(e) => setName(e.target.value)}
            />
          </FormControl>

          <FormControl label={t('common:fields.comment.label')}>
            <Textarea
              size="sm"
              h="150px"
              resize="none"
              value={comment ?? ''}
              placeholder={t('drawer.form.comment.placeholder')}
              onChange={(e) => setComment(e.target.value)}
            />
          </FormControl>

          <FormControl id="expiredAt" label={t('drawer.form.expiredAt.label')} isRequired>
            <DatePicker
              popperPlacement="top"
              dateFormat="dd MMM yyyy"
              selected={expiredAt ? new Date(expiredAt) : null}
              placeholderText={t('drawer.form.expiredAt.placeholder')}
              onChange={onDateChange}
              inputProps={{ size: 'sm' }}
            />
          </FormControl>

          <Divider />

          <FormControl label={t('common:fields.currency.label')}>
            <CurrencySelect value={currency} onChange={setCurrency} />
          </FormControl>

          <HStack w="100%" spacing={4}>
            <PriceInput
              label={t('drawer.form.amount.label')}
              placeholder={t('drawer.form.amount.placeholder')}
              currency={currency}
              defaultValue={amount ?? undefined}
              onChange={setAmount}
              isInvalid={isAmountInvalid}
              isRequired
            />

            <PriceInput
              label={t('drawer.form.taxAmount.label')}
              placeholder={t('drawer.form.taxAmount.placeholder')}
              currency={currency}
              defaultValue={taxAmount ?? undefined}
              onChange={setTaxAmount}
              isInvalid={isTaxAmountInvalid}
              isRequired
            />
          </HStack>

          <Divider />

          <FormControl id="quote" label={t('drawer.form.document.label')} isRequired>
            <FileInput
              label={t('common:fields.fileDocument.label')}
              sublabel={t('common:fields.fileDocument.documentTypes')}
              defaultIdentifier={props.quote?.id}
              defaultName={props.quote?.filename ?? undefined}
              defaultSize={props.quote?.size ?? undefined}
              isSavedFile={!!props.quote}
              contentTypes={DOCUMENT_CONTENT_TYPES}
              imageMaxWidth={1200}
              onChange={onFileChange}
              onRemove={onFileRemoved}
            />
          </FormControl>
        </VStack>
      </Drawer>
    </>
  );
}